import React, { useEffect, useState } from "react";
import { api, apiErr, fmt, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/kit";
import { FolderKanban, Clock, Truck, Package } from "lucide-react";
import { toast } from "sonner";

export default function ProjectOverview() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/overview").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  return (
    <div className="rise">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-amber-500">Sammanställning</p>
        <h1 className="font-display text-3xl font-extrabold text-white">Projektöversikt</h1>
        <p className="mt-1 text-sm text-slate-400">Totala timmar, maskintimmar och materialmängd per projekt.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {rows.map(({ project: p, summary: s }) => (
          <Card key={p.id} className="p-5" data-testid={`overview-${p.id}`}>
            <div className="mb-4 flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400"><FolderKanban size={17} /></div>
                <div>
                  <h3 className="font-display text-lg font-bold text-white">{p.name}</h3>
                  <p className="text-xs text-slate-500">{p.contractor_name || "—"} · {s.count} rapporter</p>
                </div>
              </div>
              {user?.role === "owner" && (
                <div className="text-right">
                  <div className="font-display text-lg font-bold text-amber-400">{fmt(s.subtotal)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">exkl. moms</div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-slate-950/50 px-4 py-3">
                <div className="flex items-center gap-1.5 text-xs text-slate-500"><Clock size={13} className="text-amber-400" /> Arbetstimmar</div>
                <div className="mt-1 font-display text-xl font-bold text-white">{fmtNum(s.work_hours)} h</div>
              </div>
              <div className="rounded-lg bg-slate-950/50 px-4 py-3">
                <div className="flex items-center gap-1.5 text-xs text-slate-500"><Truck size={13} className="text-sky-400" /> Maskintimmar</div>
                <div className="mt-1 font-display text-xl font-bold text-white">{fmtNum(s.machine_hours)} h</div>
              </div>
            </div>

            <div className="mt-3 rounded-lg bg-slate-950/50 px-4 py-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs text-slate-500"><Package size={13} className="text-violet-400" /> Material</div>
              {(s.materials_breakdown || []).length === 0 ? (
                <p className="text-sm text-slate-600">Inget material rapporterat.</p>
              ) : (
                <div className="space-y-1">
                  {s.materials_breakdown.map((m, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-300">{m.name}</span>
                      <span className="font-mono text-slate-200">{fmtNum(m.quantity)} {m.unit}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ))}
        {rows.length === 0 && <Card className="p-12 text-center text-slate-500">Inga projekt ännu.</Card>}
      </div>
    </div>
  );
}
